import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ShieldAlert, Check, X, Pencil, ArrowUpRight, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/apiClient";
import { toast } from "sonner";

const RISK = { high: "bg-red-100 text-red-700 border-red-200", medium: "bg-amber-100 text-amber-700 border-amber-200", low: "bg-teal-100 text-teal-700 border-teal-200" };
const STATUS = { pending: "text-amber-600", approved: "text-teal-600", rejected: "text-red-600", escalated: "text-purple-600", info_requested: "text-sky-600" };
const ACTIONS = [
  { key: "approve", label: "Approve", icon: Check, cls: "bg-teal-600 hover:bg-teal-700 text-white" },
  { key: "reject", label: "Reject", icon: X, cls: "bg-white border border-red-200 text-red-600 hover:bg-red-50" },
  { key: "edit", label: "Edit", icon: Pencil, cls: "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50" },
  { key: "escalate", label: "Escalate", icon: ArrowUpRight, cls: "bg-white border border-purple-200 text-purple-700 hover:bg-purple-50" },
  { key: "request_info", label: "Request info", icon: HelpCircle, cls: "bg-white border border-sky-200 text-sky-700 hover:bg-sky-50" },
];

export default function ApprovalCenter() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState("");
  const [status, setStatus] = useState("pending");
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState({});

  const load = useCallback(() => {
    const params = {};
    if (cat) params.category = cat;
    if (status) params.status = status;
    api.get("/approvals", { params }).then((r) => { setItems(r.data.approvals); setCats(r.data.categories); }).catch(() => toast.error("Could not load approvals"));
  }, [cat, status]);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    if (!["admin", "executive", "manager", "staff"].includes(user.role)) { navigate("/dashboard"); return; }
    load();
  }, [user, loading, navigate, load]);

  const act = async (id, action) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/approvals/${id}/action`, { action, note: notes[id] || "" });
      toast.success(`Marked ${data.status.replace("_", " ")}`);
      load();
    } catch { toast.error("Action failed"); } finally { setBusy(false); }
  };

  if (loading || !user) return <div className="py-32 grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-blue-800" /></div>;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="approval-center">
      <div className="flex items-center gap-2 mb-1"><ShieldAlert className="w-5 h-5 text-amber-500" /><p className="text-xs uppercase tracking-[0.16em] text-amber-600 font-semibold">Human-in-the-loop</p></div>
      <h1 className="font-display text-3xl lg:text-4xl font-bold text-blue-950">T5 Approval Center</h1>
      <p className="text-slate-600 mt-1">AI prepares the recommendation and evidence. You make the consequential decision.</p>

      <div className="flex flex-wrap gap-2 mt-6">
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="approval-status-filter" className="h-9 rounded-lg border border-slate-200 px-3 text-sm">
          {["pending", "approved", "rejected", "escalated", "info_requested", ""].map((s) => <option key={s} value={s}>{s ? s.replace("_", " ") : "All statuses"}</option>)}
        </select>
        <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="approval-category-filter" className="h-9 rounded-lg border border-slate-200 px-3 text-sm">
          <option value="">All categories</option>
          {cats.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      <div className="mt-6 space-y-4">
        {items.length === 0 && <div className="text-center py-16 text-slate-400" data-testid="approval-empty">No items match this filter.</div>}
        {items.map((a) => (
          <motion.div key={a.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm" data-testid={`approval-card-${a.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">{a.category}</span>
                  <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full border ${RISK[a.risk] || RISK.medium}`}>{a.risk} risk</span>
                  <span className={`text-xs font-semibold ${STATUS[a.status]}`}>{a.status.replace("_", " ")}</span>
                </div>
                <h3 className="font-display text-lg font-bold text-blue-950 mt-2">{a.title}</h3>
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-4 mt-4 text-sm">
              <div><p className="text-xs uppercase tracking-wide text-slate-400 font-semibold mb-1">What happened</p><p className="text-slate-700">{a.what_happened}</p></div>
              <div><p className="text-xs uppercase tracking-wide text-slate-400 font-semibold mb-1">AI recommends</p><p className="text-slate-700">{a.ai_recommendation}</p></div>
              <div><p className="text-xs uppercase tracking-wide text-slate-400 font-semibold mb-1">Why</p><p className="text-slate-700">{a.ai_reasoning}</p></div>
            </div>
            {a.supporting?.length > 0 && (
              <div className="mt-4 bg-slate-50 rounded-xl p-3 space-y-1.5">
                {a.supporting.map((s, i) => (
                  <div key={i} className="text-sm"><span className="font-semibold text-slate-600">{s.label}: </span><span className="text-slate-700 whitespace-pre-wrap">{s.value}</span></div>
                ))}
              </div>
            )}
            {a.status === "pending" && !["admin", "executive", "manager"].includes(user.role) && (
              <p className="mt-4 text-xs text-slate-400 italic" data-testid={`approval-readonly-${a.id}`}>View only — a manager or executive must decide this item.</p>
            )}
            {a.status === "pending" && ["admin", "executive", "manager"].includes(user.role) && (
              <div className="mt-4">
                <Textarea value={notes[a.id] || ""} onChange={(e) => setNotes({ ...notes, [a.id]: e.target.value })}
                  placeholder="Add a note (optional)…" data-testid={`approval-note-${a.id}`} className="mb-3 h-16" />
                <div className="flex flex-wrap gap-2">
                  {ACTIONS.map((act2) => {
                    const Icon = act2.icon;
                    return <Button key={act2.key} disabled={busy} onClick={() => act(a.id, act2.key)} data-testid={`approval-${act2.key}-${a.id}`} className={`gap-1.5 h-9 ${act2.cls}`}><Icon className="w-4 h-4" /> {act2.label}</Button>;
                  })}
                </div>
              </div>
            )}
            {a.status !== "pending" && a.decided_by && <p className="mt-3 text-xs text-slate-400">Decided by {a.decided_by}{a.decision_note ? ` · "${a.decision_note}"` : ""}</p>}
          </motion.div>
        ))}
      </div>
    </div>
  );
}
